export type RorInstitution = {
  id: string;
  name: string;
  country?: string;
  city?: string;
  types?: string[];
};

type RorName = {
  value?: string;
  types?: string[];
};

type RorItem = {
  id?: string;
  names?: RorName[];
  types?: string[];
  locations?: Array<{
    geonames_details?: {
      country_name?: string;
      name?: string;
    };
  }>;
};

type RorSearchResponse = {
  number_of_results?: number;
  items?: RorItem[];
  errors?: string[];
};

const ROR_API_BASE = 'https://api.ror.org/v2/organizations';
const OTHER_OPTION_ID = '__other__';

export const ROR_OTHER_OPTION: RorInstitution = {
  id: OTHER_OPTION_ID,
  name: 'Other (not listed)',
};

export function isRorOtherOption(id: string | null | undefined): boolean {
  return id === OTHER_OPTION_ID;
}

export function getRorDisplayName(item: RorItem): string {
  const names = item.names || [];
  const display = names.find(n => (n.types || []).includes('ror_display'));
  if (display?.value) return display.value;
  const label = names.find(n => (n.types || []).includes('label'));
  if (label?.value) return label.value;
  return names[0]?.value || item.id || 'Unknown organization';
}

export function mapRorItem(item: RorItem): RorInstitution | null {
  if (!item?.id) return null;
  const geo = item.locations?.[0]?.geonames_details;
  return {
    id: item.id,
    name: getRorDisplayName(item),
    country: geo?.country_name,
    city: geo?.name,
    types: item.types,
  };
}

/**
 * Search ROR organizations (education-focused when possible).
 * Public API, no auth. Caller should debounce.
 */
export async function searchRorInstitutions(
  query: string,
  options: { signal?: AbortSignal; limit?: number } = {},
): Promise<RorInstitution[]> {
  const trimmed = query.trim();
  if (trimmed.length < 2) return [];

  const params = new URLSearchParams({
    query: trimmed,
    filter: 'types:education',
  });

  const response = await fetch(`${ROR_API_BASE}?${params.toString()}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal: options.signal,
  });

  if (!response.ok) {
    throw new Error(`ROR search failed (${response.status})`);
  }

  const data = (await response.json()) as RorSearchResponse;
  if (data.errors?.length) {
    throw new Error(data.errors.join('; '));
  }

  const limit = options.limit ?? 10;
  return (data.items || [])
    .map(mapRorItem)
    .filter((item): item is RorInstitution => Boolean(item))
    .slice(0, limit);
}
